import React, { useState, useEffect } from 'react';
import { trackFunnelStep } from '../analytics';
import { trackMetaStartTrial } from '../metaPixel';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useSubscriptionProducts from '../hooks/useSubscriptionProducts';

const SubscriptionSuccess = ({ onContinue, subscriptionPlanName }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { plans, loadingPlans } = useSubscriptionProducts();

  // Match the user's actual subscription plan, fall back to highlighted or first
  const plan = (subscriptionPlanName && plans.find(p => p.name === subscriptionPlanName))
    || plans.find(p => p.highlighted)
    || plans[0];

  useEffect(() => {
    trackFunnelStep('subscription_success');
    trackMetaStartTrial(plan?.price);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const trialDays = plan?.trialPeriodDays || 7;

  const handleContinue = async () => {
    setIsProcessing(true);
    setErrorMessage('');
    try {
      const result = await onContinue();
      if (result && result.success === false) {
        setErrorMessage(result.message || 'Subscription confirmation is still pending. Please try again in a moment.');
      }
    } catch (error) {
      setErrorMessage('Something went wrong while confirming your subscription. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card sx={{ textAlign: 'center', boxShadow: 3 }}>
          <CardContent sx={{ py: 6 }}>
            <CheckCircleIcon
              sx={{
                fontSize: 80,
                color: 'success.main',
                mb: 3,
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: '#2c3e50',
              }}
            >
              Welcome to Reseller Pro!
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ mb: 1 }}
            >
              Your {trialDays}-day free trial has started successfully.
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mb: 4 }}
            >
              You won't be charged until {new Date(Date.now() + trialDays * 24 * 60 * 60 * 1000).toLocaleDateString()}
            </Typography>

            <Alert severity="info" sx={{ mb: 4, textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                What's included in your {plan?.name || 'subscription'}:
              </Typography>
              {loadingPlans ? (
                <Box>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} variant="text" width="80%" sx={{ mb: 0.5 }} />
                  ))}
                </Box>
              ) : plan?.features?.length > 0 ? (
                <List dense disablePadding>
                  {plan.features.map((feature, idx) => (
                    <ListItem key={idx} sx={{ py: 0.25, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2">
                  Full access to all features during your trial period.
                </Typography>
              )}
            </Alert>

            <Typography
              variant="body2"
              color="textSecondary"
              sx={{ mb: 3 }}
            >
              Next, you'll create your first sales channel to get started.
            </Typography>

            {errorMessage && (
              <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                {errorMessage}
              </Alert>
            )}

            <Button
              variant="contained"
              size="large"
              onClick={handleContinue}
              disabled={isProcessing}
              sx={{
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 700,
                py: 1.5,
                px: 4,
              }}
            >
              {isProcessing ? 'Confirming Subscription...' : 'Create Your First Sales Channel'}
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default SubscriptionSuccess;
